import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Circles } from "react-loader-spinner";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import PaymentIcon from "@mui/icons-material/Payment";
import ImageIcon from "@mui/icons-material/Image";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import getDecryptedToken from "../../api/getDecryptedToken";
import baseUrl from "../../api/baseUrl";

type ClinicData = {
  clinicName?: string;
  clinicId?: string;
  address?: string;
  registerNo?: string;
  ownerName?: string;
  tpNo?: string;
  title?: string;
  regNoMethod?: string;
  regNoFormat?: string;
  reqNoStart?: string;
  startTime?: string;
  endTime?: string;
  timeDuration?: string;
  employeeCount?: number;
  advancedChart?: boolean;
  appointmentApp?: boolean;
  appDocLimit?: number;
  logo?: boolean;
  logoimage?: string;
  printImages?: string;
  headerImages?: string;
  footerImages?: string;
  queueVideos?: {
    originalName?: string;
    mime?: string;
    size?: number;
    uploadedAt?: string;
  }[];
  createdAt?: string;
  updatedAt?: string;
};

type PaymentData = {
  price?: number | string;
  paymentendDate?: string;
  packageName?: string;
  startDate?: string;
  endDate?: string;
  free?: boolean;
  active?: boolean;
};

type StorageImage = {
  url: string | null;
  sizeBytes: number;
  type: string;
  label: string;
};

type StorageSummaryRow = {
  id: string;
  label: string;
  sizeBytes: number;
  objectCount: number;
  percentOfTotal: number;
};

type StorageUsage = {
  sizeBytes: number;
  objectCount: number;
  scannedAt?: string;
  summary?: StorageSummaryRow[];
  bookingSite?: {
    sizeBytes: number;
    objectCount: number;
    status?: string | null;
    publicUrl?: string;
    images: StorageImage[];
  } | null;
};

const IMAGE_TYPE_LABELS: Record<string, string> = {
  gallery: "Gallery",
  "before-after": "Before / after",
  dentist: "Dentist photo",
  booking: "Booking",
};

const STORAGE_CATEGORY_ICONS: Record<string, React.ElementType> = {
  branding: PaletteOutlinedIcon,
  staff: GroupsOutlinedIcon,
  patients: PersonIcon,
  treatments: LocalHospitalOutlinedIcon,
  booking: BookOnlineIcon,
  other: FolderOutlinedIcon,
};

type AccountTab = "clinic" | "profile" | "subscription" | "storage";

const ACCOUNT_TABS: {
  id: AccountTab;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  description: string;
}[] = [
  {
    id: "clinic",
    label: "Clinic",
    shortLabel: "Clinic",
    icon: BusinessIcon,
    description: "Practice details & branding",
  },
  {
    id: "profile",
    label: "My profile",
    shortLabel: "Profile",
    icon: PersonIcon,
    description: "Your account information",
  },
  {
    id: "subscription",
    label: "Subscription",
    shortLabel: "Plan",
    icon: PaymentIcon,
    description: "Billing & package",
  },
  {
    id: "storage",
    label: "File storage",
    shortLabel: "Storage",
    icon: StorageOutlinedIcon,
    description: "Usage & media files",
  },
];

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function formatDate(value?: string | Date | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatList(items?: string[] | null) {
  if (!items?.length) return "—";
  const list = items.map((s) => String(s).trim()).filter(Boolean);
  return list.length ? list.join(", ") : "—";
}

function PanelCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-200/80 bg-white shadow-sm shadow-slate-200/40 dark:border-slate-700/80 dark:bg-slate-800/60 dark:shadow-none ${className}`}
    >
      {children}
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
        <Icon sx={{ fontSize: 20 }} />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white tracking-tight">
          {title}
        </h3>
        {subtitle ? (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

function SubsectionTitle({ children }: { children: string }) {
  return (
    <h4 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-8 mb-3 first:mt-0">
      <span className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent dark:from-slate-600" />
      <span className="shrink-0">{children}</span>
      <span className="h-px flex-1 bg-gradient-to-l from-slate-200 to-transparent dark:from-slate-600" />
    </h4>
  );
}

function DetailRow({ label, value }: { label: string; value?: React.ReactNode }) {
  const display =
    value === undefined || value === null || value === "" ? "—" : value;
  return (
    <div className="group flex flex-col gap-1 rounded-xl px-3 py-2.5 transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-700/30 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-sm font-semibold text-slate-800 dark:text-slate-100 sm:text-right break-words">
        {display}
      </dd>
    </div>
  );
}

function DetailsBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/50 divide-y divide-slate-100/80 dark:border-slate-700/60 dark:bg-slate-900/30 dark:divide-slate-700/50">
      {children}
    </div>
  );
}

function BoolBadge({ value }: { value?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
        value
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800/50"
          : "bg-slate-100 text-slate-600 ring-1 ring-slate-200/60 dark:bg-slate-700/50 dark:text-slate-300 dark:ring-slate-600/50"
      }`}
    >
      {value ? (
        <>
          <VerifiedOutlinedIcon sx={{ fontSize: 12 }} />
          Yes
        </>
      ) : (
        "No"
      )}
    </span>
  );
}

function MetricTile({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        accent
          ? "border-primary/20 bg-gradient-to-br from-primary/[0.07] to-white dark:from-primary/15 dark:to-slate-800/80 dark:border-primary/30"
          : "border-slate-100 bg-white dark:border-slate-700/60 dark:bg-slate-800/40"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p
        className={`mt-1.5 text-xl font-bold tracking-tight ${
          accent ? "text-primary" : "text-slate-900 dark:text-white"
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function AccountTabsNav({
  active,
  onChange,
  layout = "bar",
}: {
  active: AccountTab;
  onChange: (tab: AccountTab) => void;
  layout?: "bar" | "sidebar";
}) {
  const isSidebar = layout === "sidebar";

  return (
    <div
      className={
        isSidebar
          ? "flex flex-col gap-1 p-4"
          : "border-b border-slate-100 bg-slate-50/60 px-3 py-3 dark:border-slate-700/80 dark:bg-slate-900/40 sm:px-4 xl:hidden"
      }
      role="tablist"
      aria-label="Account sections"
    >
      <div
        className={
          isSidebar
            ? "flex flex-col gap-1"
            : "flex gap-1 overflow-x-auto pb-0.5 scrollbar-thin"
        }
      >
        {ACCOUNT_TABS.map(({ id, label, shortLabel, icon: Icon, description }) => {
          const isActive = active === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              title={description}
              onClick={() => onChange(id)}
              className={`relative flex rounded-xl text-left transition-all duration-200 ${
                isSidebar
                  ? `w-full items-center gap-3 px-4 py-3.5 ${
                      isActive
                        ? "bg-primary text-white shadow-md shadow-primary/25"
                        : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800/80"
                    }`
                  : `min-w-[7.5rem] flex-1 flex-col items-start gap-0.5 px-3.5 py-2.5 sm:min-w-0 sm:flex-none sm:items-center sm:gap-2 sm:px-4 ${
                      isActive
                        ? "bg-white text-primary shadow-md shadow-slate-200/60 ring-1 ring-slate-200/80 dark:bg-slate-800 dark:text-primary dark:shadow-slate-900/40 dark:ring-slate-600/80"
                        : "text-slate-600 hover:bg-white/70 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800/50 dark:hover:text-slate-200"
                    }`
              }`}
            >
              <Icon
                sx={{ fontSize: isSidebar ? 22 : 18 }}
                className={isActive && isSidebar ? "text-white" : isActive ? "text-primary" : ""}
              />
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-semibold ${isSidebar ? "" : ""}`}>
                  {isSidebar ? label : (
                    <>
                      <span className="hidden sm:inline">{label}</span>
                      <span className="sm:hidden">{shortLabel}</span>
                    </>
                  )}
                </span>
                {isSidebar ? (
                  <span
                    className={`mt-0.5 block text-[11px] font-medium leading-snug ${
                      isActive ? "text-white/80" : "text-slate-400 dark:text-slate-500"
                    }`}
                  >
                    {description}
                  </span>
                ) : (
                  <span
                    className={`hidden text-[10px] font-medium sm:block ${
                      isActive ? "text-slate-500 dark:text-slate-400" : "text-slate-400 dark:text-slate-500"
                    }`}
                  >
                    {description}
                  </span>
                )}
              </span>
              {isActive && !isSidebar ? (
                <span className="absolute bottom-0 left-3 right-3 hidden h-0.5 rounded-full bg-primary sm:block" />
              ) : null}
              {isActive && isSidebar ? (
                <span className="h-2 w-2 shrink-0 rounded-full bg-white/90" aria-hidden />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProfileHero({
  photoUrl,
  initials,
  fullName,
  roleName,
  chips,
}: {
  photoUrl: string | null;
  initials: string;
  fullName: string;
  roleName?: string;
  chips: { label: string; on: boolean }[];
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-primary/[0.04] p-6 dark:border-slate-700/80 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-primary/10">
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/5 blur-2xl"
        aria-hidden
      />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center xl:gap-8">
        {photoUrl ? (
          <div className="relative shrink-0">
            <img
              src={photoUrl}
              alt={fullName}
              className="h-28 w-28 rounded-2xl object-cover ring-4 ring-white shadow-lg dark:ring-slate-700"
            />
            <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-white dark:ring-slate-800">
              <VerifiedOutlinedIcon sx={{ fontSize: 14 }} />
            </span>
          </div>
        ) : (
          <div className="flex h-28 w-28 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primaryLight text-3xl font-bold text-white shadow-lg shadow-primary/25 ring-4 ring-white dark:ring-slate-700">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white capitalize">
            {fullName}
          </p>
          {roleName ? (
            <span className="mt-2 inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary dark:bg-primary/20">
              {roleName}
            </span>
          ) : null}
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-4 xl:gap-3">
            {chips.map(({ label, on }) => (
              <div
                key={label}
                className="rounded-xl border border-slate-200/80 bg-white/80 px-3 py-2 backdrop-blur-sm dark:border-slate-600/60 dark:bg-slate-900/40"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {label}
                </p>
                <div className="mt-1">
                  <BoolBadge value={on} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ClinicTabPanel({
  clinic,
  clinicNameFallback,
  addressFallback,
  phoneFallback,
  logoUrl,
}: {
  clinic: ClinicData | null;
  clinicNameFallback?: string;
  addressFallback?: string;
  phoneFallback?: string;
  logoUrl: string | null;
}) {
  const queueCount = clinic?.queueVideos?.length ?? 0;
  const displayName = clinic?.clinicName || clinicNameFallback || "—";

  return (
    <div className="space-y-6 xl:space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-primary/[0.04] p-6 xl:p-8 dark:border-slate-700/80 dark:from-slate-800/80 dark:via-slate-800/60 dark:to-primary/10">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center xl:gap-8">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Clinic logo"
              className="h-24 w-24 shrink-0 rounded-2xl border border-slate-200/80 bg-white object-contain p-2 shadow-sm dark:border-slate-600 xl:h-28 xl:w-28"
            />
          ) : (
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primaryLight shadow-lg shadow-primary/20 xl:h-28 xl:w-28">
              <BusinessIcon sx={{ fontSize: 40 }} className="text-white" />
            </div>
          )}
          <div className="min-w-0 flex-1 xl:flex xl:items-end xl:justify-between xl:gap-6">
            <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              Clinic profile
            </p>
            <p className="mt-1 text-2xl font-bold capitalize tracking-tight text-slate-900 dark:text-white xl:text-3xl">
              {displayName}
            </p>
            {clinic?.clinicId ? (
              <p className="mt-1.5 inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                ID {clinic.clinicId}
              </p>
            ) : null}
            {(clinic?.address || addressFallback) && (
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 xl:max-w-xl">
                {clinic?.address || addressFallback}
              </p>
            )}
            </div>
            <div className="mt-4 hidden xl:grid xl:grid-cols-2 xl:gap-3 xl:mt-0">
              <MetricTile label="Staff limit" value={clinic?.employeeCount ?? "—"} />
              <MetricTile
                label="Appointment app"
                value={<BoolBadge value={clinic?.appointmentApp} />}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="xl:grid xl:grid-cols-12 xl:gap-6 xl:items-start">
        <PanelCard className="p-5 sm:p-6 xl:col-span-7 xl:p-7">
          <SectionHeader icon={BusinessIcon} title="General information" />
          <div className="xl:grid xl:grid-cols-2 xl:gap-6">
            <div>
              <SubsectionTitle>General</SubsectionTitle>
              <DetailsBlock>
                <DetailRow label="Clinic name" value={displayName} />
                <DetailRow label="Clinic ID" value={clinic?.clinicId} />
                <DetailRow label="Bill / print title" value={clinic?.title} />
                <DetailRow label="Owner name" value={clinic?.ownerName} />
                <DetailRow label="Address" value={clinic?.address || addressFallback} />
                <DetailRow label="Telephone" value={clinic?.tpNo || phoneFallback} />
              </DetailsBlock>
            </div>
            <div>
              <SubsectionTitle>Registration &amp; hours</SubsectionTitle>
              <DetailsBlock>
                <DetailRow label="Registration no." value={clinic?.registerNo} />
                <DetailRow label="Logo on documents" value={<BoolBadge value={clinic?.logo} />} />
                <DetailRow label="Reg. no. method" value={clinic?.regNoMethod} />
                <DetailRow label="Reg. no. format" value={clinic?.regNoFormat} />
                <DetailRow label="Starting number" value={clinic?.reqNoStart} />
                <DetailRow label="Start time" value={clinic?.startTime} />
                <DetailRow label="End time" value={clinic?.endTime} />
                <DetailRow label="Slot duration" value={clinic?.timeDuration} />
              </DetailsBlock>
            </div>
          </div>
        </PanelCard>

        <div className="space-y-6 xl:col-span-5">
          <PanelCard className="p-5 sm:p-6 xl:p-7">
            <SectionHeader
              icon={SettingsOutlinedIcon}
              title="Features & limits"
              subtitle="Enabled modules and capacity"
            />
            <div className="grid grid-cols-2 gap-3">
              <MetricTile
                label="Advanced chart"
                value={<BoolBadge value={clinic?.advancedChart} />}
              />
              <MetricTile
                label="Appointment app"
                value={<BoolBadge value={clinic?.appointmentApp} />}
              />
              <MetricTile label="App doctor limit" value={clinic?.appDocLimit ?? "—"} />
              <MetricTile label="Staff limit" value={clinic?.employeeCount ?? "—"} />
            </div>
            <div className="mt-4">
              <DetailsBlock>
                <DetailRow
                  label="Queue screen videos"
                  value={
                    queueCount > 0 ? `${queueCount} video${queueCount === 1 ? "" : "s"}` : "—"
                  }
                />
              </DetailsBlock>
              {queueCount > 0 ? (
                <ul className="mt-3 space-y-2">
                  {clinic!.queueVideos!.map((v, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2 text-sm dark:border-slate-700/60 dark:bg-slate-900/30"
                    >
                      <span className="font-medium text-slate-800 dark:text-slate-200">
                        {v.originalName || `Video ${i + 1}`}
                      </span>
                      {v.size != null ? (
                        <span className="text-xs text-slate-500">{formatBytes(v.size)}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </PanelCard>

          <PanelCard className="p-5 sm:p-6 xl:p-7">
            <SectionHeader icon={ScheduleOutlinedIcon} title="Record" />
            <DetailsBlock>
              <DetailRow label="Created" value={formatDate(clinic?.createdAt)} />
              <DetailRow label="Last updated" value={formatDate(clinic?.updatedAt)} />
            </DetailsBlock>
          </PanelCard>
        </div>
      </div>

      {(clinic?.printImages || clinic?.headerImages || clinic?.footerImages) && (
        <PanelCard className="p-5 sm:p-6 xl:p-7">
          <SectionHeader icon={PaletteOutlinedIcon} title="Branding images" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3">
            {[
              { label: "Print image", src: clinic?.printImages },
              { label: "Header image", src: clinic?.headerImages },
              { label: "Footer image", src: clinic?.footerImages },
            ].map(
              ({ label, src }) =>
                src && (
                  <div
                    key={label}
                    className="overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50 dark:border-slate-600/80 dark:bg-slate-900/40"
                  >
                    <p className="border-b border-slate-100 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:border-slate-700">
                      {label}
                    </p>
                    <img
                      src={src}
                      alt={label}
                      className="max-h-40 w-full object-contain p-3"
                    />
                  </div>
                )
            )}
          </div>
        </PanelCard>
      )}
    </div>
  );
}

type EmployeeProfile = {
  title?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  address?: string;
  employeeId?: string;
  image?: string | null;
  imageUrl?: string | null;
  IsDentist?: boolean;
  IsBookDentist?: boolean;
  hasAppointmentApp?: boolean;
  status?: boolean;
  studies?: string[];
  specialities?: string[];
  languages?: string[];
  color?: string;
  assignedTreatmentIds?: string[];
  roleId?: { roleName?: string };
  createdAt?: string;
  updatedAt?: string;
};

function ProfileTabPanel({
  employee,
  loading,
  fallbackName,
  fallbackRole,
}: {
  employee: EmployeeProfile | null;
  loading: boolean;
  fallbackName?: string;
  fallbackRole?: string;
}) {
  const photoUrl =
    (typeof employee?.imageUrl === "string" && employee.imageUrl) ||
    (typeof employee?.image === "string" && employee.image.startsWith("http")
      ? employee.image
      : null);

  const fullName = employee
    ? [employee.title?.trim(), employee.firstName?.trim(), employee.lastName?.trim()]
        .filter(Boolean)
        .join(" ")
    : fallbackName || "—";

  const roleName = employee?.roleId?.roleName || fallbackRole;
  const initials =
    `${employee?.firstName?.charAt(0) || fallbackName?.charAt(0) || "U"}${(employee?.lastName?.charAt(0) || "").toUpperCase()}`.toUpperCase();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <Circles height="44" width="44" color="#174d7a" ariaLabel="loading profile" />
        <p className="text-sm text-slate-500">Loading your profile…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProfileHero
        photoUrl={photoUrl}
        initials={initials}
        fullName={fullName}
        roleName={roleName}
        chips={[
          { label: "Dentist", on: !!employee?.IsDentist },
          { label: "Book dentist", on: !!employee?.IsBookDentist },
          { label: "Appointment app", on: !!employee?.hasAppointmentApp },
          { label: "Active", on: employee?.status !== false },
        ]}
      />

      <div className="xl:grid xl:grid-cols-12 xl:gap-6 xl:items-start">
        <PanelCard className="p-5 sm:p-6 xl:col-span-5 xl:p-7">
          <SectionHeader icon={PersonIcon} title="Contact & credentials" />
          <DetailsBlock>
            <DetailRow label="Employee ID" value={employee?.employeeId} />
            <DetailRow label="Email" value={employee?.email} />
            <DetailRow label="Phone" value={employee?.phoneNumber} />
            <DetailRow label="Address" value={employee?.address} />
          </DetailsBlock>
        </PanelCard>

        <PanelCard className="p-5 sm:p-6 xl:col-span-4 xl:p-7">
          <SectionHeader icon={LocalHospitalOutlinedIcon} title="Professional details" />
          <DetailsBlock>
            <DetailRow label="Studies" value={formatList(employee?.studies)} />
            <DetailRow label="Specialities" value={formatList(employee?.specialities)} />
            <DetailRow label="Languages" value={formatList(employee?.languages)} />
            <DetailRow
              label="Assigned treatments"
              value={
                employee?.assignedTreatmentIds?.length
                  ? `${employee.assignedTreatmentIds.length} selected`
                  : undefined
              }
            />
            {employee?.color ? (
              <div className="flex flex-col gap-2 rounded-xl px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                <dt className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Calendar color
                </dt>
                <dd className="flex items-center gap-2 sm:justify-end">
                  <span
                    className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 shadow-inner dark:border-slate-600"
                    style={{ backgroundColor: employee.color }}
                    aria-hidden
                  />
                  <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {employee.color}
                  </span>
                </dd>
              </div>
            ) : null}
          </DetailsBlock>
        </PanelCard>

        <PanelCard className="p-5 sm:p-6 xl:col-span-3 xl:p-7">
          <SectionHeader icon={ScheduleOutlinedIcon} title="Account timeline" />
          <DetailsBlock>
            <DetailRow label="Member since" value={formatDate(employee?.createdAt)} />
            <DetailRow label="Last updated" value={formatDate(employee?.updatedAt)} />
          </DetailsBlock>
        </PanelCard>
      </div>
    </div>
  );
}

function SubscriptionTabPanel({
  payment,
  onManagePayment,
}: {
  payment: PaymentData | null;
  onManagePayment: () => void;
}) {
  const isActive = payment?.active ?? true;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile
          label="Current package"
          value={payment?.packageName || "—"}
          accent
        />
        <MetricTile
          label="Monthly rental"
          value={
            payment?.price != null && payment.price !== ""
              ? `LKR ${payment.price}`
              : "—"
          }
        />
        <MetricTile
          label="Subscription ends"
          value={formatDate(payment?.paymentendDate)}
        />
        <MetricTile
          label="Status"
          value={<BoolBadge value={isActive} />}
        />
      </div>

      <div className="xl:grid xl:grid-cols-12 xl:gap-6 xl:items-stretch">
        <PanelCard className="p-5 sm:p-6 xl:col-span-7 xl:p-7">
          <SectionHeader icon={PaymentIcon} title="Plan details" />
          <div className="xl:grid xl:grid-cols-2 xl:gap-6">
            <DetailsBlock>
              <DetailRow label="Period start" value={formatDate(payment?.startDate)} />
              <DetailRow label="Period end" value={formatDate(payment?.endDate)} />
            </DetailsBlock>
            <DetailsBlock>
              <DetailRow label="Free package" value={<BoolBadge value={payment?.free} />} />
              <DetailRow label="Active subscription" value={<BoolBadge value={isActive} />} />
            </DetailsBlock>
          </div>
        </PanelCard>

        <div className="flex flex-col justify-center rounded-2xl border border-dashed border-primary/30 bg-gradient-to-br from-primary/[0.06] to-primaryLight/5 p-6 xl:col-span-5 dark:border-primary/40 dark:from-primary/10 dark:to-slate-800/40">
          <PaymentIcon sx={{ fontSize: 36 }} className="text-primary mb-3" />
          <p className="text-lg font-bold text-slate-900 dark:text-white">
            Manage billing & payments
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 mb-5">
            Update payment method, view invoices, or renew your subscription.
          </p>
          <button
            type="button"
            onClick={onManagePayment}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-md shadow-primary/25 transition hover:opacity-95 hover:shadow-lg hover:shadow-primary/30 sm:w-auto"
          >
            <PaymentIcon sx={{ fontSize: 18 }} />
            Manage payment
          </button>
        </div>
      </div>
    </div>
  );
}

function StorageTabPanel({
  storage,
  storageLoading,
  storageError,
  onRefresh,
}: {
  storage: StorageUsage | null;
  storageLoading: boolean;
  storageError: string | null;
  onRefresh: () => void;
}) {
  const summaryRows = storage?.summary ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-white">
            File storage overview
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Space used by your clinic across all categories
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={storageLoading}
          className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 sm:self-auto"
        >
          <RefreshIcon
            sx={{ fontSize: 16 }}
            className={storageLoading ? "animate-spin text-primary" : ""}
          />
          Refresh scan
        </button>
      </div>

      {storageLoading && !storage ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <Circles height="44" width="44" color="#174d7a" ariaLabel="loading storage" />
          <p className="text-sm text-slate-500">Analyzing storage usage…</p>
        </div>
      ) : storageError ? (
        <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
          {storageError}
        </div>
      ) : storage ? (
        <div className="xl:grid xl:grid-cols-12 xl:gap-6 xl:items-start">
          <div className="space-y-6 xl:col-span-5 2xl:col-span-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
            <MetricTile label="Total used" value={formatBytes(storage.sizeBytes)} accent />
            <MetricTile
              label="Total files"
              value={storage.objectCount.toLocaleString()}
            />
            <MetricTile
              label="Last scanned"
              value={
                storage.scannedAt
                  ? new Date(storage.scannedAt).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })
                  : "—"
              }
            />
          </div>

          {summaryRows.length > 0 ? (
            <PanelCard className="overflow-hidden">
              <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
                <SectionHeader
                  icon={StorageOutlinedIcon}
                  title="Usage by category"
                  subtitle="Breakdown of stored files"
                />
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700/80">
                {summaryRows.map((row) => {
                  const CatIcon = STORAGE_CATEGORY_ICONS[row.id] || FolderOutlinedIcon;
                  return (
                    <div key={row.id} className="px-5 py-4">
                      <div className="mb-3 flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
                          <CatIcon sx={{ fontSize: 18 }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                              {row.label}
                            </span>
                            <span className="text-sm font-bold text-primary">
                              {formatBytes(row.sizeBytes)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {row.objectCount} file{row.objectCount === 1 ? "" : "s"} ·{" "}
                            {row.percentOfTotal}% of total
                          </p>
                        </div>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary to-primaryLight transition-all duration-500"
                          style={{
                            width: `${Math.min(100, Math.max(2, row.percentOfTotal))}%`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </PanelCard>
          ) : null}
          </div>

          <div className="mt-6 space-y-6 xl:col-span-7 xl:mt-0 2xl:col-span-8">
          {storage.bookingSite && (
            <PanelCard className="overflow-hidden h-full">
              <div className="border-b border-slate-100 bg-gradient-to-r from-primary/[0.06] to-transparent px-5 py-4 dark:border-slate-700/80 dark:from-primary/10">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm dark:bg-slate-800">
                      <BookOnlineIcon sx={{ fontSize: 22 }} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                        Booking page images
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        Gallery & before/after photos on your public booking page
                      </p>
                      {storage.bookingSite.status ? (
                        <span
                          className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                            String(storage.bookingSite.status).toLowerCase() === "published"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                              : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          }`}
                        >
                          {storage.bookingSite.status}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="rounded-lg bg-white px-2.5 py-1 font-bold text-primary shadow-sm dark:bg-slate-800">
                      {formatBytes(storage.bookingSite.sizeBytes)}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {storage.bookingSite.objectCount} file
                      {storage.bookingSite.objectCount === 1 ? "" : "s"}
                    </span>
                    {storage.bookingSite.publicUrl ? (
                      <a
                        href={storage.bookingSite.publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-95"
                      >
                        View booking page
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="p-5">
                {storage.bookingSite.images.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-3 2xl:grid-cols-4">
                    {storage.bookingSite.images.map((img, index) => (
                      <div
                        key={`${img.type}-${index}`}
                        className="group overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition hover:shadow-md dark:border-slate-600/80 dark:bg-slate-800"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-900">
                          {img.url ? (
                            <img
                              src={img.url}
                              alt={IMAGE_TYPE_LABELS[img.type] || img.label}
                              className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center">
                              <ImageIcon
                                className="text-slate-300 dark:text-slate-600"
                                sx={{ fontSize: 32 }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="border-t border-slate-100 px-2.5 py-2 dark:border-slate-700">
                          <p className="truncate text-[10px] font-bold uppercase tracking-wide text-primary">
                            {IMAGE_TYPE_LABELS[img.type] || img.label}
                          </p>
                          <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                            {formatBytes(img.sizeBytes)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 py-12 text-center dark:border-slate-600">
                    <ImageIcon className="text-slate-300 dark:text-slate-600" sx={{ fontSize: 40 }} />
                    <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">
                      No booking page images uploaded yet
                    </p>
                  </div>
                )}
              </div>
            </PanelCard>
          )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Standalone /my-account sits outside Layouts; body is overflow:hidden — this is the scrollport. */
function AccountPageScroll({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const dental = document.getElementById("dental");
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    const prevDental = dental?.style.overflow ?? "";

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    if (dental) dental.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
      if (dental) dental.style.overflow = prevDental;
    };
  }, []);

  return (
    <div
      className="account-page-scroll fixed inset-0 z-0 overflow-y-auto overflow-x-hidden bg-[#f8f9fb] overscroll-y-contain dark:bg-slate-950"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {children}
    </div>
  );
}

function MyAccount() {
  const navigate = useNavigate();
  const [clinic, setClinic] = useState<ClinicData | null>(null);
  const [payment, setPayment] = useState<PaymentData | null>(null);
  const [storage, setStorage] = useState<StorageUsage | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [storageLoading, setStorageLoading] = useState(true);
  const [employee, setEmployee] = useState<EmployeeProfile | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AccountTab>("clinic");

  const eid = getDecryptedToken("eid");
  const userName = getDecryptedToken("userName");
  const userRole = getDecryptedToken("role");
  const clinicNameToken = getDecryptedToken("clinicName");
  const clinicPhone = getDecryptedToken("clinicPhoneNumber");
  const userAddress = getDecryptedToken("address");
  const clinicLogo = getDecryptedToken("clinicLogo");
  const showLogo = getDecryptedToken("logo") === "true";

  useEffect(() => {
    document.title = clinicNameToken
      ? `${clinicNameToken} | My Account`
      : "My Account";
  }, [clinicNameToken]);

  const loadStorage = () => {
    setStorageLoading(true);
    setStorageError(null);
    return baseUrl
      .get("/client/account/aws-storage")
      .then((res) => {
        if (res?.data?.success) {
          setStorage(res.data.data as StorageUsage);
        } else {
          setStorage(null);
          setStorageError("Could not load storage usage.");
        }
      })
      .catch(() => {
        setStorage(null);
        setStorageError("Could not load storage usage.");
      })
      .finally(() => setStorageLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStorageLoading(true);

    const employeeReq = eid
      ? baseUrl.get("/employee/findEmployeeById", { headers: { employeeid: eid } })
      : Promise.resolve(null);

    Promise.all([
      baseUrl.get("/clinic/getBillData"),
      baseUrl.get("/client/fee/getClinicPaymentbyId").catch(() => null),
      baseUrl.get("/client/account/aws-storage").catch(() => null),
      employeeReq.catch(() => null),
    ])
      .then(([clinicRes, paymentRes, storageRes, employeeRes]) => {
        if (cancelled) return;
        setClinic(clinicRes?.data?.data ?? null);
        if (employeeRes?.data?.success) {
          setEmployee(employeeRes.data.data as EmployeeProfile);
        } else {
          setEmployee(null);
        }

        const payPayload = paymentRes?.data?.data;
        const pkg = payPayload?.result;
        setPayment(
          pkg
            ? {
                price: payPayload?.price ?? pkg?.price,
                paymentendDate:
                  payPayload?.paymentendDate ?? pkg?.paymentendDate,
                packageName: pkg?.packageName,
                startDate: pkg?.startDate,
                endDate: pkg?.endDate,
                free: pkg?.free,
                active: pkg?.active,
              }
            : payPayload
              ? {
                  price: payPayload.price,
                  paymentendDate: payPayload.paymentendDate,
                }
              : null
        );

        if (storageRes?.data?.success) {
          setStorage(storageRes.data.data as StorageUsage);
          setStorageError(null);
        } else {
          setStorage(null);
          setStorageError("Could not load storage usage.");
        }
      })
      .catch(() => {
        if (!cancelled) setError("Could not load account details. Please try again.");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setStorageLoading(false);
          setUserLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eid]);

  const logoUrl = useMemo(() => {
    if (clinic?.logoimage) return clinic.logoimage;
    if (showLogo && clinicLogo) {
      const api = process.env.REACT_APP_API ?? "";
      return `${api}${clinicLogo}`;
    }
    return null;
  }, [clinic?.logoimage, clinicLogo, showLogo]);

  const activeTabMeta = ACCOUNT_TABS.find((t) => t.id === activeTab);

  if (loading) {
    return (
      <AccountPageScroll>
        <div className="flex min-h-full w-full flex-col items-center justify-center gap-4 py-16">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-primary/10 blur-xl scale-150" aria-hidden />
            <Circles height="56" width="56" color="#174d7a" ariaLabel="loading" />
          </div>
          <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
            Loading your account…
          </p>
        </div>
      </AccountPageScroll>
    );
  }

  return (
    <AccountPageScroll>
    <div className="flex w-full min-h-full flex-col">
      {/* Top bar — scrolls inside .layout-outlet-scroll (app shell) */}
      <header className="relative shrink-0 overflow-hidden border-b border-slate-200/80 bg-white dark:border-slate-700/80 dark:bg-slate-800/90">
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent dark:from-primary/15"
          aria-hidden
        />
        <div className="relative flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5 xl:px-8">
          <div className="flex items-center gap-4 min-w-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Clinic logo"
                className="h-14 w-14 shrink-0 rounded-xl border border-slate-200/80 bg-white object-contain p-1 shadow-sm dark:border-slate-600 xl:h-16 xl:w-16"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primaryLight shadow-md xl:h-16 xl:w-16">
                <BusinessIcon sx={{ fontSize: 28 }} className="text-white" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                Account center
              </p>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white xl:text-3xl">
                My Account
              </h1>
              <p className="mt-0.5 truncate text-sm font-semibold capitalize text-slate-600 dark:text-slate-300">
                {clinic?.clinicName || clinicNameToken || "—"}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:items-end">
            <button
              type="button"
              onClick={() => navigate("/client/dashboard")}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary/25 transition hover:opacity-95 hover:shadow-lg hover:shadow-primary/30"
            >
              <DashboardOutlinedIcon sx={{ fontSize: 18 }} />
              Go to clinic
            </button>
            <p className="hidden max-w-md text-right text-sm text-slate-500 dark:text-slate-400 xl:block">
              Manage your clinic profile, subscription, and file storage in one place.
            </p>
          </div>
        </div>
      </header>

      {error && (
        <div
          className="mx-4 mt-4 rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200 sm:mx-6 xl:mx-8"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Shell: sidebar + main — page scroll handled by layout-outlet-scroll */}
      <div className="flex flex-col xl:flex-row xl:items-start">
        {/* Desktop sidebar — sticks while scrolling */}
        <aside
          className="sticky top-4 z-10 hidden w-72 shrink-0 flex-col self-start border-r border-slate-200/80 bg-slate-50/50 dark:border-slate-700/80 dark:bg-slate-900/30 xl:flex"
          aria-label="Account navigation"
        >
          <div className="border-b border-slate-200/60 px-4 py-4 dark:border-slate-700/60">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Sections
            </p>
          </div>
          <AccountTabsNav
            active={activeTab}
            onChange={setActiveTab}
            layout="sidebar"
          />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Mobile / tablet tabs */}
          <div className="sticky top-0 z-10 shrink-0 border-b border-slate-200/80 bg-white dark:border-slate-700/80 dark:bg-slate-800/50 xl:hidden">
            <AccountTabsNav active={activeTab} onChange={setActiveTab} layout="bar" />
          </div>

          {/* Content header */}
          <div className="shrink-0 border-b border-slate-100 bg-white/80 px-4 py-4 backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-800/40 sm:px-6 xl:px-8 xl:py-5">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white xl:text-xl">
              {activeTabMeta?.label}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {activeTabMeta?.description}
            </p>
          </div>

          <main
            className="bg-slate-50/40 p-4 pb-10 dark:bg-slate-900/20 sm:p-6 sm:pb-12 xl:p-8 xl:pb-14"
            role="tabpanel"
          >
            {activeTab === "clinic" && (
              <ClinicTabPanel
                clinic={clinic}
                clinicNameFallback={clinicNameToken}
                addressFallback={userAddress}
                phoneFallback={clinicPhone}
                logoUrl={logoUrl}
              />
            )}
            {activeTab === "profile" && (
              <ProfileTabPanel
                employee={employee}
                loading={userLoading}
                fallbackName={userName}
                fallbackRole={userRole}
              />
            )}
            {activeTab === "subscription" && (
              <SubscriptionTabPanel
                payment={payment}
                onManagePayment={() => navigate("/client/pay")}
              />
            )}
            {activeTab === "storage" && (
              <StorageTabPanel
                storage={storage}
                storageLoading={storageLoading}
                storageError={storageError}
                onRefresh={loadStorage}
              />
            )}
          </main>
        </div>
      </div>
    </div>
    </AccountPageScroll>
  );
}

export default MyAccount;
